import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Optional: Suppress unwanted console errors
const originalConsoleError = console.error;
console.error = (...args) => {
  const suppressPatterns = [
    "Grammarly",
    "Iterable",
    "place:1",
    "Failed to load resource",
  ];
  const message = args[0];
  if (
    typeof message === "string" &&
    suppressPatterns.some((pattern) => message.includes(pattern))
  ) {
    return; // skip logging
  }
  originalConsoleError(...args); // log other errors
};

const CustomerContactForm = () => {
  const formRef = useRef(null);
  const navigate = useNavigate();
  const [formHeight, setFormHeight] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [enquiry, setEnquiry] = useState("");
  const [resumeFile, setResumeFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    subject: "",
    message: "",
    ourLocation: "",
    workLocation: "",
  });

  useEffect(() => {
    if (formRef.current) setFormHeight(formRef.current.clientHeight);
    const handleResize = () => {
      if (formRef.current) setFormHeight(formRef.current.clientHeight);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleEnquiryChange = (e) => {
    setEnquiry(e.target.value);
    setErrors((prev) => ({ ...prev, enquiry: undefined }));
  };

  const validateFile = (file) => {
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/jpeg",
      "image/png",
    ];
    if (!file) {
      setErrors((prev) => ({ ...prev, resumeFile: "Resume is required" }));
      return false;
    }
    if (!allowedTypes.includes(file.type)) {
      setErrors((prev) => ({ ...prev, resumeFile: "Invalid file type" }));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});
    setSubmitted(false);

    if (!validateFile(resumeFile)) {
      setIsLoading(false);
      return;
    }

    if (!enquiry) {
      setErrors((prev) => ({ ...prev, enquiry: "Please select an enquiry." }));
      setIsLoading(false);
      return;
    }

    // Create AbortController for request cancellation
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    try {
      const data = new FormData();
      data.append("firstName", formData.firstName);
      data.append("lastName", formData.lastName);
      data.append("phone", formData.phone);
      data.append("email", formData.email);
      data.append("subject", formData.subject);
      data.append("message", formData.message);
      data.append("enquiry", enquiry);
      data.append("ourLocation", formData.ourLocation);
      data.append("workLocation", formData.workLocation);
      data.append("resume", resumeFile);

      const response = await fetch("https://getform.io/f/bejlqgka", {
        method: "POST",
        body: data,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        console.log(
          "%c✅ Email sent successfully. Status: 200",
          "color: green; font-weight: bold;"
        );

        setSubmitted(true);
        setFormData({
          firstName: "",
          lastName: "",
          phone: "",
          email: "",
          subject: "",
          message: "",
          ourLocation: "",
          workLocation: "",
        });
        setEnquiry("");
        setResumeFile(null);
        formRef.current.reset();

        setTimeout(() => {
          if (enquiry === "IT" || enquiry === "Non-IT") {
            navigate(`/careers/open-positions?type=${enquiry}`);
          } else {
            navigate("/careers/open-positions");
          }
        }, 1500);
      } else {
        setErrors((prev) => ({
          ...prev,
          email: "Submission failed. Please try again later.",
        }));
      }
    } catch (error) {
      clearTimeout(timeoutId);

      if (error.name === 'AbortError') {
        setErrors((prev) => ({
          ...prev,
          email: "Request timed out. Please check your connection and try again.",
        }));
      } else {
        setErrors((prev) => ({
          ...prev,
          email: "Network error. Please try again later.",
        }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full px-4 sm:px-8 md:px-16 py-12 bg-white">
      <h2 className="text-3xl font-semibold text-center mb-10">
        Get in Touch with Us
      </h2>
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
        <div
          className="flex justify-center items-start"
          style={{ height: `${formHeight}px` }}
        >
          <img
            src="https://thumbs.dreamstime.com/b/busy-professional-employees-working-office-laptop-talking-vertical-two-busy-professional-business-people-working-305307676.jpg"
            alt="Customer Interaction"
            className="w-full h-full object-contain rounded-lg"
          />
        </div>
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              placeholder="First Name*"
              className="border p-2 rounded"
            />
            <input
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
              placeholder="Last Name*"
              className="border p-2 rounded"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              required
              placeholder="Phone*"
              className="border p-2 rounded"
            />
            <input
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Email*"
              className="border p-2 rounded"
            />
          </div>
          <input
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            required
            placeholder="Subject*"
            className="border p-2 w-full rounded"
          />
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            placeholder="Description*"
            className="border p-2 w-full h-24 rounded resize-none"
          />
          <div>
            <select
              value={enquiry}
              onChange={handleEnquiryChange}
              required
              className="border p-2 w-full rounded"
            >
              <option value="">Select Enquiry*</option>
              <option value="IT">IT</option>
              <option value="Non-IT">Non-IT</option>
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              name="ourLocation"
              value={formData.ourLocation}
              onChange={handleChange}
              placeholder="Current Location*"
              className="border p-2 rounded"
              required
            />
            <input
              name="workLocation"
              value={formData.workLocation}
              onChange={handleChange}
              placeholder="Work Location*"
              className="border p-2 rounded"
              required
            />
          </div>
          {errors.enquiry && (
            <p className="text-red-600 text-sm">{errors.enquiry}</p>
          )}
          <div>
            <label className="block font-medium">
              Upload Resume<span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.png"
              onChange={(e) => {
                setResumeFile(e.target.files[0]);
                setErrors((prev) => ({ ...prev, resumeFile: undefined }));
              }}
              className="border p-2 w-full rounded"
            />
            {errors.resumeFile && (
              <p className="text-red-600 text-sm">{errors.resumeFile}</p>
            )}
          </div>
          <div className="space-y-2 text-sm text-gray-700">
            <label className="flex items-start space-x-2">
              <input type="checkbox" className="mt-1" />
              <span>I consent to receive marketing communications.</span>
            </label>
            <label className="flex items-start space-x-2">
              <input type="checkbox" className="mt-1" required />
              <span>
                I have read and understood the{" "}
                <a href="/privacy" className="text-cyan-500 underline">
                  Privacy Policy
                </a>
                .
              </span>
            </label>
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className={`bg-cyan-500 text-white px-6 py-2 rounded-full hover:bg-cyan-600 transition ${
              isLoading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isLoading ? "Submitting..." : "Submit →"}
          </button>
          {submitted && (
            <p className="text-green-600 font-medium mt-3">
              ✅ Thank you! Your message has been sent.
            </p>
          )}
          {errors.email && (
            <p className="text-red-600 font-medium mt-3">{errors.email}</p>
          )}
        </form>
      </div>
    </div>
  );
};

export default CustomerContactForm;